import type {
  FlowPoint,
  RosterEdgeAttachments,
} from 'components/IncidentConfig/roster/rosterEdgeAttach';
import {
  AREA_OR_DISTRICT_COMMAND_SEAT,
  COMMAND_STAFF_LEFT_SEATS,
  COMMAND_STAFF_RIGHT_SEATS,
  INCIDENT_COMMANDER_SEAT,
  TEMA_COMMAND_STAFF_LEFT_SEATS,
  TEMA_COMMAND_STAFF_RIGHT_SEATS,
  TEMA_DACO_SEAT,
  TEMA_DIRECTOR_SEAT,
  compareIcsSeatNames,
  compareTemaSectionNames,
  isCommandStaffSeat,
  isSectionChiefSeat,
  isTemaDirectorSeat,
  isTemaRccSeat,
  isTemaSectionSeat,
} from 'components/IncidentConfig/roster/rosterLayoutTiers';
import { NODE_LEFT_GUTTER } from 'components/IncidentConfig/roster/seatBoxMetrics';

export type RosterLayoutStyle = 'centered' | 'left' | 'right';

export type AlignedSide = 'left' | 'right';

export const LAYOUT_STYLE_OPTIONS: {
  id: RosterLayoutStyle;
  label: string;
  hint: string;
}[] = [
  {
    id: 'centered',
    label: 'Centered',
    hint: 'ICS layout · flanked command staff · section chiefs on a crossbar',
  },
  {
    id: 'left',
    label: 'Left Aligned',
    hint: 'Children indent 20% right · connectors exit parent left edge',
  },
  {
    id: 'right',
    label: 'Right Aligned',
    hint: 'Children indent 20% left · connectors exit parent right edge',
  },
];

/**
 * One seat as far as the layout is concerned: where it sits in the tree and how
 * big its box is.
 */
export type LayoutNode = {
  id: string;
  parentId: string | null;
  name: string;
  width: number;
  height: number;
};

export type LayoutResult = {
  layoutPositions: Record<string, FlowPoint>;
  edgeRoutes: Record<string, FlowPoint[]>;
  edgeAttachments: Record<string, RosterEdgeAttachments>;
};

/** Children sit this fraction of the parent's width in from the aligned edge. */
const ALIGNED_INDENT_RATIO = 0.2;
/** Connector meets the box this far down its side, clear of the corner. */
const ALIGNED_ATTACH_OFFSET = 0.1;
const V_GAP = 40;
const CENTERED_H_GAP = 40;
/**
 * Gap between ICS section-chief columns. Larger than CENTERED_H_GAP so
 * left-indented subtrees (ALIGNED_INDENT_RATIO) and their left-rail connectors
 * clear the neighboring section instead of overlapping it.
 */
const SECTION_H_GAP = 120;
/**
 * Extra space above the section row so the shared crossbar sits clear of box
 * tops.
 */
const SECTION_CROSSBAR_HEADROOM = 24;
/**
 * Clear half-width of the IC spine corridor so flanked command staff never sit
 * on the vertical drop to the section crossbar.
 */
const SPINE_HALF_GUTTER = 56;
/** Separation between two independent trees when the whole chart is restyled. */
const ROOT_GAP = 80;
const COLLISION_MARGIN = 20;
const COLLISION_THRESHOLD = 0.5;
const COLLISION_MAX_ITERATIONS = 200;

const COMMAND_STAFF_LEFT_SET = new Set<string>([
  ...COMMAND_STAFF_LEFT_SEATS,
  ...TEMA_COMMAND_STAFF_LEFT_SEATS,
]);
const COMMAND_STAFF_RIGHT_SET = new Set<string>([
  ...COMMAND_STAFF_RIGHT_SEATS,
  ...TEMA_COMMAND_STAFF_RIGHT_SEATS,
]);
const TEMA_COMMAND_STAFF_LEFT_ORDER =
  TEMA_COMMAND_STAFF_LEFT_SEATS as readonly string[];
const TEMA_COMMAND_STAFF_RIGHT_ORDER =
  TEMA_COMMAND_STAFF_RIGHT_SEATS as readonly string[];
const TEMA_RCC_ORDER = [
  'West RCC',
  'Middle RCC',
  'East RCC',
  'Southeast RCC',
] as const;

export function rosterEdgeId(parentId: string, childId: string): string {
  return `e-${parentId}-${childId}`;
}

export function isAlignedStyle(style: RosterLayoutStyle): style is AlignedSide {
  return style === 'left' || style === 'right';
}

/** Seeded HWCG title. Older charts may still use the short branch name. */
export const SOURCE_CONTROL_BRANCH_DIRECTOR_SEAT =
  'Source Control Branch Director';
export const SOURCE_CONTROL_BRANCH_SEAT = 'Source Control Branch';

export function isHwcgSourceControlSeat(name: string): boolean {
  return (
    name === SOURCE_CONTROL_BRANCH_DIRECTOR_SEAT ||
    name === SOURCE_CONTROL_BRANCH_SEAT
  );
}

export function isHwcgSourceControlChart(nodes: { name: string }[]): boolean {
  return nodes.some((node) => isHwcgSourceControlSeat(node.name));
}

type LayoutModel = {
  byId: Map<string, LayoutNode>;
  childrenOf: Map<string, string[]>;
  roots: string[];
};

/**
 * Children keep the left-to-right order they already have on screen, so
 * restyling rearranges the chart without reshuffling siblings the user has
 * grown used to. Seats that have never been placed fall back to name order.
 */
function buildModel(
  nodes: LayoutNode[],
  existingLayout: Record<string, FlowPoint>
): LayoutModel {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const childrenOf = new Map<string, string[]>();
  const roots: string[] = [];

  for (const node of nodes) {
    const hasParent = node.parentId != null && byId.has(node.parentId);
    if (!hasParent) {
      roots.push(node.id);
      continue;
    }
    const siblings = childrenOf.get(node.parentId!) ?? [];
    siblings.push(node.id);
    childrenOf.set(node.parentId!, siblings);
  }

  const compare = (a: string, b: string) => {
    const ax = existingLayout[a]?.x;
    const bx = existingLayout[b]?.x;
    if (ax != null && bx != null && ax !== bx) return ax - bx;
    if (ax != null && bx == null) return -1;
    if (ax == null && bx != null) return 1;
    return (byId.get(a)?.name ?? '').localeCompare(byId.get(b)?.name ?? '');
  };

  for (const siblings of childrenOf.values()) siblings.sort(compare);
  roots.sort(compare);

  return { byId, childrenOf, roots };
}

/** Selected seats whose ancestors are not also selected. */
export function getLayoutSelectionRoots(
  selectedIds: string[],
  parentById: Record<string, string | null>
): string[] {
  const selected = new Set(selectedIds);
  return selectedIds.filter((id) => {
    let parent = parentById[id] ?? null;
    const seen = new Set<string>();
    while (parent && !seen.has(parent)) {
      if (selected.has(parent)) return false;
      seen.add(parent);
      parent = parentById[parent] ?? null;
    }
    return true;
  });
}

function layoutCenteredSubtree(
  model: LayoutModel,
  rootId: string,
  rootPosition: FlowPoint
): Record<string, FlowPoint> {
  const placed: Record<string, FlowPoint> = { [rootId]: { ...rootPosition } };
  const widthCache = new Map<string, number>();

  const subtreeWidth = (id: string, seen: Set<string>): number => {
    const cached = widthCache.get(id);
    if (cached != null) return cached;

    const own = model.byId.get(id)?.width ?? 0;
    const children = seen.has(id) ? [] : (model.childrenOf.get(id) ?? []);
    const nextSeen = new Set(seen).add(id);
    const childWidths = children.map((child) => subtreeWidth(child, nextSeen));
    const total =
      childWidths.reduce((sum, width) => sum + width, 0) +
      CENTERED_H_GAP * Math.max(0, children.length - 1);
    const width = Math.max(own, total);
    widthCache.set(id, width);
    return width;
  };

  const place = (parentId: string, seen: Set<string>) => {
    const parent = placed[parentId];
    const parentNode = model.byId.get(parentId);
    if (!parent || !parentNode) return;

    const children = model.childrenOf.get(parentId) ?? [];
    if (children.length === 0) return;

    const widths = children.map((child) => subtreeWidth(child, new Set()));
    const totalWidth =
      widths.reduce((sum, width) => sum + width, 0) +
      CENTERED_H_GAP * Math.max(0, children.length - 1);

    let cursorX = parent.x + parentNode.width / 2 - totalWidth / 2;
    const childY = parent.y + parentNode.height + V_GAP;

    children.forEach((child, index) => {
      if (seen.has(child)) return;
      const slotWidth = widths[index];
      const childWidth = model.byId.get(child)?.width ?? 0;
      placed[child] = {
        x: cursorX + slotWidth / 2 - childWidth / 2,
        y: childY,
      };
      place(child, new Set(seen).add(child));
      cursorX += slotWidth + CENTERED_H_GAP;
    });
  };

  place(rootId, new Set([rootId]));
  return placed;
}

function layoutAlignedSubtree(
  model: LayoutModel,
  rootId: string,
  rootPosition: FlowPoint,
  side: AlignedSide
): Record<string, FlowPoint> {
  const placed: Record<string, FlowPoint> = { [rootId]: { ...rootPosition } };

  const placeSubtree = (parentId: string, seen: Set<string>): number => {
    const parent = placed[parentId];
    const parentNode = model.byId.get(parentId);
    if (!parent || !parentNode) return parent?.y ?? 0;

    const parentBottom = parent.y + parentNode.height;
    const children = model.childrenOf.get(parentId) ?? [];
    if (children.length === 0) return parentBottom;

    const indentMagnitude = parentNode.width * ALIGNED_INDENT_RATIO;
    const childX =
      parent.x + (side === 'left' ? indentMagnitude : -indentMagnitude);

    let childY = parentBottom + V_GAP;
    let bottom = parentBottom;

    for (const child of children) {
      if (seen.has(child)) continue;
      placed[child] = { x: childX, y: childY };
      bottom = placeSubtree(child, new Set(seen).add(child));
      childY = bottom + V_GAP;
    }

    return bottom;
  };

  placeSubtree(rootId, new Set([rootId]));
  return placed;
}

function layoutSubtree(
  model: LayoutModel,
  style: RosterLayoutStyle,
  rootId: string,
  rootPosition: FlowPoint
): Record<string, FlowPoint> {
  return isAlignedStyle(style)
    ? layoutAlignedSubtree(model, rootId, rootPosition, style)
    : layoutCenteredSubtree(model, rootId, rootPosition);
}

/**
 * How far a left-rail subtree reaches either side of its root, including the
 * add-child button hanging off the left edge. A crossbar slot sized to the root
 * box alone is only wide enough while the branch is shallow: every level
 * indents again, so a deep branch grows into the next column and the collision
 * pass has to shove seats off their rail to separate them.
 */
function alignedSubtreeExtent(
  model: LayoutModel,
  rootId: string,
  side: AlignedSide
): { minX: number; maxX: number } {
  const subtree = layoutAlignedSubtree(model, rootId, { x: 0, y: 0 }, side);
  const { minX, maxX } = boundsOf(model, subtree);
  return { minX: minX - NODE_LEFT_GUTTER, maxX };
}

/**
 * Lay a row of seats out on a shared crossbar, centred under `centerX`. Each
 * one gets a slot as wide as everything that hangs under it, so the subtrees
 * laid out afterwards cannot reach their neighbours.
 */
function placeCrossbarRow(input: {
  model: LayoutModel;
  placed: Record<string, FlowPoint>;
  ids: string[];
  centerX: number;
  topY: number;
  side: AlignedSide;
}): void {
  const { model, placed, ids, centerX, topY, side } = input;
  if (ids.length === 0) return;

  const slots = ids.map((id) => {
    const extent = alignedSubtreeExtent(model, id, side);
    return { id, offset: -extent.minX, width: extent.maxX - extent.minX };
  });
  const totalWidth =
    slots.reduce((sum, slot) => sum + slot.width, 0) +
    SECTION_H_GAP * Math.max(0, slots.length - 1);

  let cursorX = centerX - totalWidth / 2;
  for (const slot of slots) {
    placed[slot.id] = { x: cursorX + slot.offset, y: topY };
    cursorX += slot.width + SECTION_H_GAP;
  }
}

/**
 * Connectors for the outline styles. Each one leaves the parent's aligned edge
 * and runs to the same side of the child, with one elbow so the line reads as a
 * bracket rather than a diagonal.
 */
function alignedConnectors(
  model: LayoutModel,
  layoutPositions: Record<string, FlowPoint>,
  side: AlignedSide,
  restyled: Set<string>
): Pick<LayoutResult, 'edgeRoutes' | 'edgeAttachments'> {
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};

  for (const node of model.byId.values()) {
    const parentId = node.parentId;
    if (!parentId || !restyled.has(node.id)) continue;

    const parentPosition = layoutPositions[parentId];
    const childPosition = layoutPositions[node.id];
    const parentNode = model.byId.get(parentId);
    if (!parentPosition || !childPosition || !parentNode) continue;

    const edgeId = rosterEdgeId(parentId, node.id);
    const sourceX =
      side === 'left' ? parentPosition.x : parentPosition.x + parentNode.width;

    edgeAttachments[edgeId] = {
      source: { side, offset: ALIGNED_ATTACH_OFFSET },
      target: { side, offset: ALIGNED_ATTACH_OFFSET },
    };
    edgeRoutes[edgeId] = [
      { x: sourceX, y: childPosition.y + node.height * ALIGNED_ATTACH_OFFSET },
    ];
  }

  return { edgeRoutes, edgeAttachments };
}

/** Rail connectors for everything hanging under `parents`, on one indent side. */
function branchConnectors(
  model: LayoutModel,
  layoutPositions: Record<string, FlowPoint>,
  parents: string[],
  side: AlignedSide
): Pick<LayoutResult, 'edgeRoutes' | 'edgeAttachments'> {
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};

  for (const parentId of parents) {
    const descendants = getDescendants(model, parentId);
    if (descendants.length === 0) continue;
    const aligned = alignedConnectors(
      model,
      layoutPositions,
      side,
      new Set(descendants)
    );
    Object.assign(edgeRoutes, aligned.edgeRoutes);
    Object.assign(edgeAttachments, aligned.edgeAttachments);
  }

  return { edgeRoutes, edgeAttachments };
}

type CollisionBox = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

function getDescendants(model: LayoutModel, id: string): string[] {
  const result: string[] = [];
  const seen = new Set<string>([id]);
  const queue = [...(model.childrenOf.get(id) ?? [])];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || seen.has(current)) continue;
    seen.add(current);
    result.push(current);
    queue.push(...(model.childrenOf.get(current) ?? []));
  }
  return result;
}

function isAncestorOf(
  model: LayoutModel,
  ancestorId: string,
  id: string
): boolean {
  let cursor = model.byId.get(id)?.parentId;
  const seen = new Set<string>([id]);
  while (cursor && !seen.has(cursor)) {
    if (cursor === ancestorId) return true;
    seen.add(cursor);
    cursor = model.byId.get(cursor)?.parentId;
  }
  return false;
}

/**
 * The branch a collision should move. Anchors are the columns the layout placed
 * deliberately — command staff and the crossbar row — so an overlap deep in a
 * branch slides the whole column and every seat stays on its rail.
 */
function branchRootFor(
  model: LayoutModel,
  id: string,
  anchors: Set<string> | undefined
): string {
  if (!anchors || anchors.size === 0) return id;
  let cursor: string | null | undefined = id;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    if (anchors.has(cursor)) return cursor;
    seen.add(cursor);
    cursor = model.byId.get(cursor)?.parentId;
  }
  return id;
}

function buildCollisionBoxes(
  model: LayoutModel,
  layout: Record<string, FlowPoint>
): CollisionBox[] {
  return Object.entries(layout).map(([id, position]) => {
    const node = model.byId.get(id);
    return {
      id,
      // The add-child button hangs off the left edge, outside the box a node
      // reports. Left out of the reckoning it lands on whatever seat sits to
      // the left, which reads as an overlap even though the boxes clear.
      x: position.x - COLLISION_MARGIN - NODE_LEFT_GUTTER,
      y: position.y - COLLISION_MARGIN,
      width: (node?.width ?? 0) + COLLISION_MARGIN * 2 + NODE_LEFT_GUTTER,
      height: (node?.height ?? 0) + COLLISION_MARGIN * 2,
    };
  });
}

function overlapOf(
  a: CollisionBox,
  b: CollisionBox
): { px: number; py: number } | null {
  const px =
    (a.width + b.width) * 0.5 -
    Math.abs(a.x + a.width * 0.5 - (b.x + b.width * 0.5));
  const py =
    (a.height + b.height) * 0.5 -
    Math.abs(a.y + a.height * 0.5 - (b.y + b.height * 0.5));

  return px > COLLISION_THRESHOLD && py > COLLISION_THRESHOLD
    ? { px, py }
    : null;
}

/**
 * Pushes overlapping branches apart sideways. Needed because restyling one
 * selected subtree leaves the rest of the chart where it was, and the restyled
 * branch is free to land on top of it.
 */
function resolveCollisions(
  model: LayoutModel,
  layoutPositions: Record<string, FlowPoint>,
  branchAnchors?: Set<string>
): Record<string, FlowPoint> {
  const next = { ...layoutPositions };
  const boxes = buildCollisionBoxes(model, next);
  const boxById = new Map(boxes.map((box) => [box.id, box]));
  const branchIds = new Map<string, string[]>();

  // Boxes are moved in step with the layout rather than rebuilt each pass, so
  // two overlaps found in the same sweep cannot both act on stale coordinates
  // and push the same seat twice.
  const shiftBranch = (id: string, deltaX: number) => {
    if (deltaX === 0) return;
    let ids = branchIds.get(id);
    if (!ids) {
      ids = [id, ...getDescendants(model, id)];
      branchIds.set(id, ids);
    }
    for (const target of ids) {
      const position = next[target];
      if (position) next[target] = { x: position.x + deltaX, y: position.y };
      const box = boxById.get(target);
      if (box) box.x += deltaX;
    }
  };

  for (
    let iteration = 0;
    iteration < COLLISION_MAX_ITERATIONS;
    iteration += 1
  ) {
    let moved = false;

    for (let i = 0; i < boxes.length; i += 1) {
      for (let j = i + 1; j < boxes.length; j += 1) {
        const a = boxes[i];
        const b = boxes[j];
        const overlap = overlapOf(a, b);
        if (!overlap) continue;

        const aBranch = branchRootFor(model, a.id, branchAnchors);
        const bBranch = branchRootFor(model, b.id, branchAnchors);
        const aFirst = a.x + a.width * 0.5 <= b.x + b.width * 0.5;
        // Two seats in the same column separate on their own rather than
        // dragging the column they share.
        const [leftId, rightId] =
          aBranch === bBranch
            ? aFirst
              ? [a.id, b.id]
              : [b.id, a.id]
            : aFirst
              ? [aBranch, bBranch]
              : [bBranch, aBranch];
        if (leftId === rightId) continue;

        // Moving a branch moves everything under it, so when one seat is the
        // other's ancestor only the descendant can close the gap.
        if (isAncestorOf(model, leftId, rightId)) {
          shiftBranch(rightId, overlap.px);
        } else if (isAncestorOf(model, rightId, leftId)) {
          shiftBranch(leftId, -overlap.px);
        } else {
          const half = overlap.px / 2;
          shiftBranch(leftId, -half);
          shiftBranch(rightId, half);
        }
        moved = true;
      }
    }

    if (!moved) break;
  }

  return next;
}

function boundsOf(
  model: LayoutModel,
  positions: Record<string, FlowPoint>
): { minX: number; maxX: number } {
  let minX = Infinity;
  let maxX = -Infinity;
  for (const [id, position] of Object.entries(positions)) {
    const width = model.byId.get(id)?.width ?? 0;
    minX = Math.min(minX, position.x);
    maxX = Math.max(maxX, position.x + width);
  }
  return Number.isFinite(minX) ? { minX, maxX } : { minX: 0, maxX: 0 };
}

/**
 * Split command staff into EMSI left/right flanks. Known seats use the
 * catalogued sides; anything else alternates onto the shorter flank.
 */
function splitCommandStaffFlanks(
  model: LayoutModel,
  commandStaffIds: string[]
): { left: string[]; right: string[] } {
  const left: string[] = [];
  const right: string[] = [];
  const extras: string[] = [];

  for (const id of commandStaffIds) {
    const name = model.byId.get(id)?.name ?? '';
    if (COMMAND_STAFF_LEFT_SET.has(name)) left.push(id);
    else if (COMMAND_STAFF_RIGHT_SET.has(name)) right.push(id);
    else extras.push(id);
  }

  const byFlankOrder = (order: readonly string[]) => (a: string, b: string) => {
    const ai = order.indexOf(model.byId.get(a)?.name ?? '');
    const bi = order.indexOf(model.byId.get(b)?.name ?? '');
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return compareIcsSeatNames(
      model.byId.get(a)?.name ?? '',
      model.byId.get(b)?.name ?? ''
    );
  };

  const hasTemaFlanks = commandStaffIds.some((id) => {
    const name = model.byId.get(id)?.name ?? '';
    return (
      TEMA_COMMAND_STAFF_LEFT_ORDER.includes(name) ||
      TEMA_COMMAND_STAFF_RIGHT_ORDER.includes(name)
    );
  });
  left.sort(
    byFlankOrder(
      hasTemaFlanks ? TEMA_COMMAND_STAFF_LEFT_ORDER : COMMAND_STAFF_LEFT_SEATS
    )
  );
  right.sort(
    byFlankOrder(
      hasTemaFlanks ? TEMA_COMMAND_STAFF_RIGHT_ORDER : COMMAND_STAFF_RIGHT_SEATS
    )
  );

  for (const id of extras) {
    if (left.length <= right.length) left.push(id);
    else right.push(id);
  }

  return { left, right };
}

/**
 * Which way a flanked seat's own reports indent, so they lead away from the
 * spine.
 */
function flankAlignSide(flank: AlignedSide): AlignedSide {
  return flank === 'left' ? 'right' : 'left';
}

/**
 * Stack command staff on either side of a clear IC spine, each with its own
 * reports underneath it. Returns the bottom Y of the taller flank including
 * those reports (or the IC bottom when there is no staff), which is what keeps
 * the section crossbar from being drawn straight through them.
 */
function placeFlankedCommandStaff(
  model: LayoutModel,
  placed: Record<string, FlowPoint>,
  commandStaffIds: string[],
  icPos: FlowPoint,
  icWidth: number,
  icHeight: number
): { bottom: number; left: string[]; right: string[] } {
  if (commandStaffIds.length === 0) {
    return { bottom: icPos.y + icHeight, left: [], right: [] };
  }

  const { left, right } = splitCommandStaffFlanks(model, commandStaffIds);
  const icCenterX = icPos.x + icWidth / 2;
  const topY = icPos.y + icHeight + V_GAP;
  let bottom = icPos.y + icHeight;

  const placeFlank = (ids: string[], flank: AlignedSide) => {
    const alignSide = flankAlignSide(flank);
    let cursorY = topY;

    for (const id of ids) {
      const node = model.byId.get(id);
      if (!node) continue;

      const position = {
        x:
          flank === 'left'
            ? icCenterX - SPINE_HALF_GUTTER - node.width
            : icCenterX + SPINE_HALF_GUTTER,
        y: cursorY,
      };
      placed[id] = position;

      let flankBottom = cursorY + node.height;
      const subtree = layoutAlignedSubtree(model, id, position, alignSide);
      for (const [childId, point] of Object.entries(subtree)) {
        if (childId === id) continue;
        placed[childId] = point;
        flankBottom = Math.max(
          flankBottom,
          point.y + (model.byId.get(childId)?.height ?? 0)
        );
      }

      bottom = Math.max(bottom, flankBottom);
      cursorY = flankBottom + V_GAP;
    }
  };

  placeFlank(left, 'left');
  placeFlank(right, 'right');

  return { bottom, left, right };
}

/**
 * IC → command staff: drop the spine, then branch horizontally into the inner
 * side of each flanked seat.
 */
function buildCommandStaffConnectors(
  model: LayoutModel,
  layout: Record<string, FlowPoint>,
  icId: string,
  commandStaffIds: string[]
): Pick<LayoutResult, 'edgeRoutes' | 'edgeAttachments'> {
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};
  const icPos = layout[icId];
  const icNode = model.byId.get(icId);
  if (!icPos || !icNode) return { edgeRoutes, edgeAttachments };

  const icCenterX = icPos.x + icNode.width / 2;

  for (const childId of commandStaffIds) {
    const childPos = layout[childId];
    const childNode = model.byId.get(childId);
    if (!childPos || !childNode) continue;

    const childCenterX = childPos.x + childNode.width / 2;
    const isLeft = childCenterX < icCenterX;
    const attachY = childPos.y + childNode.height * ALIGNED_ATTACH_OFFSET;
    const edgeId = rosterEdgeId(icId, childId);

    edgeAttachments[edgeId] = {
      source: { side: 'bottom', offset: 0.5 },
      target: {
        side: isLeft ? 'right' : 'left',
        offset: ALIGNED_ATTACH_OFFSET,
      },
    };
    edgeRoutes[edgeId] = [{ x: icCenterX, y: attachY }];
  }

  return { edgeRoutes, edgeAttachments };
}

/**
 * Shared horizontal crossbar under the command-staff band for every IC direct
 * report that is not command staff (section chiefs and other seats).
 */
function buildCrossbarConnectors(
  model: LayoutModel,
  layout: Record<string, FlowPoint>,
  icId: string,
  sectionRowIds: string[],
  crossbarY: number
): Pick<LayoutResult, 'edgeRoutes' | 'edgeAttachments'> {
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};
  const icPos = layout[icId];
  const icNode = model.byId.get(icId);
  if (!icPos || !icNode) return { edgeRoutes, edgeAttachments };

  const icCenterX = icPos.x + icNode.width / 2;

  for (const childId of sectionRowIds) {
    const childPos = layout[childId];
    const childNode = model.byId.get(childId);
    if (!childPos || !childNode) continue;

    const edgeId = rosterEdgeId(icId, childId);
    edgeAttachments[edgeId] = {
      source: { side: 'bottom', offset: 0.5 },
      target: { side: 'top', offset: 0.5 },
    };
    edgeRoutes[edgeId] = [
      { x: icCenterX, y: crossbarY },
      { x: childPos.x + childNode.width / 2, y: crossbarY },
    ];
  }

  return { edgeRoutes, edgeAttachments };
}

/**
 * TEMA State EOC: TEMA Director → SEID/PSC flanks + DACO spine → section
 * crossbar under DACO → left-rail section subtrees → RCC bottom band.
 */
export function layoutTemaEocOrgChart(input: {
  nodes: LayoutNode[];
}): LayoutResult {
  const model = buildModel(input.nodes, {});
  const placed: Record<string, FlowPoint> = {};
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};

  const byName = (name: string) =>
    input.nodes.find((node) => node.name === name);

  const director = byName(TEMA_DIRECTOR_SEAT);
  if (!director) {
    return { layoutPositions: {}, edgeRoutes, edgeAttachments };
  }

  placed[director.id] = { x: 0, y: 0 };
  const directorPos = placed[director.id];

  const directorChildren = [...(model.childrenOf.get(director.id) ?? [])].sort(
    (a, b) =>
      compareIcsSeatNames(
        model.byId.get(a)?.name ?? '',
        model.byId.get(b)?.name ?? ''
      )
  );

  const commandStaff = directorChildren.filter((id) =>
    isCommandStaffSeat(model.byId.get(id)?.name ?? '')
  );
  const dacoId = directorChildren.find(
    (id) => model.byId.get(id)?.name === TEMA_DACO_SEAT
  );
  const otherUnderDirector = directorChildren.filter(
    (id) => !commandStaff.includes(id) && id !== dacoId
  );

  const flanks = placeFlankedCommandStaff(
    model,
    placed,
    commandStaff,
    directorPos,
    director.width,
    director.height
  );
  const commandStaffBottom = flanks.bottom;

  const staffConnectors = buildCommandStaffConnectors(
    model,
    placed,
    director.id,
    commandStaff
  );
  Object.assign(edgeRoutes, staffConnectors.edgeRoutes);
  Object.assign(edgeAttachments, staffConnectors.edgeAttachments);

  const dacoNode = dacoId ? model.byId.get(dacoId) : undefined;
  if (dacoId && dacoNode) {
    const dacoY =
      Math.max(commandStaffBottom, directorPos.y + director.height) + V_GAP;
    placed[dacoId] = {
      x: directorPos.x + director.width / 2 - dacoNode.width / 2,
      y: dacoY,
    };

    const dacoEdgeId = rosterEdgeId(director.id, dacoId);
    edgeAttachments[dacoEdgeId] = {
      source: { side: 'bottom', offset: 0.5 },
      target: { side: 'top', offset: 0.5 },
    };
  }

  // Any unexpected TEMA Director children (not flanks / DACO) sit on a row
  // beside DACO so they are not lost.
  if (otherUnderDirector.length > 0) {
    const dacoPlaced = dacoId != null ? placed[dacoId] : undefined;
    const anchorY =
      dacoPlaced?.y ??
      Math.max(commandStaffBottom, directorPos.y + director.height) + V_GAP;
    let cursorX =
      (dacoPlaced
        ? dacoPlaced.x + (model.byId.get(dacoId!)?.width ?? 0)
        : directorPos.x + director.width) + SECTION_H_GAP;
    for (const id of otherUnderDirector) {
      const node = model.byId.get(id);
      if (!node) continue;
      placed[id] = { x: cursorX, y: anchorY };
      cursorX += node.width + SECTION_H_GAP;
    }
  }

  const spineId = dacoId ?? director.id;
  const spinePos = placed[spineId];
  const spineNode = model.byId.get(spineId);
  if (!spinePos || !spineNode) {
    return { layoutPositions: placed, edgeRoutes, edgeAttachments };
  }

  const spineChildren = [...(model.childrenOf.get(spineId) ?? [])].sort(
    (a, b) => {
      const an = model.byId.get(a)?.name ?? '';
      const bn = model.byId.get(b)?.name ?? '';
      if (isTemaSectionSeat(an) || isTemaSectionSeat(bn)) {
        return compareTemaSectionNames(an, bn);
      }
      return compareIcsSeatNames(an, bn);
    }
  );

  const sectionRowIds = spineChildren.filter((id) =>
    isTemaSectionSeat(model.byId.get(id)?.name ?? '')
  );
  const rccIds = spineChildren
    .filter((id) => isTemaRccSeat(model.byId.get(id)?.name ?? ''))
    .sort((a, b) => {
      const an = model.byId.get(a)?.name ?? '';
      const bn = model.byId.get(b)?.name ?? '';
      const ai = (TEMA_RCC_ORDER as readonly string[]).indexOf(an);
      const bi = (TEMA_RCC_ORDER as readonly string[]).indexOf(bn);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return an.localeCompare(bn);
    });
  const otherUnderSpine = spineChildren.filter(
    (id) => !sectionRowIds.includes(id) && !rccIds.includes(id)
  );

  const sectionGapAbove = spinePos.y + spineNode.height + V_GAP;
  const sectionBandTop = sectionGapAbove + SECTION_CROSSBAR_HEADROOM;
  const crossbarY = (sectionGapAbove + sectionBandTop) / 2;

  const crossbarIds = [...sectionRowIds, ...otherUnderSpine];
  if (crossbarIds.length > 0) {
    placeCrossbarRow({
      model,
      placed,
      ids: crossbarIds,
      centerX: spinePos.x + spineNode.width / 2,
      topY: sectionBandTop,
      side: 'left',
    });

    const crossbar = buildCrossbarConnectors(
      model,
      placed,
      spineId,
      crossbarIds,
      crossbarY
    );
    Object.assign(edgeRoutes, crossbar.edgeRoutes);
    Object.assign(edgeAttachments, crossbar.edgeAttachments);
  }

  for (const parentId of crossbarIds) {
    const parentPos = placed[parentId];
    if (!parentPos) continue;
    const subtree = layoutAlignedSubtree(model, parentId, parentPos, 'left');
    for (const [id, point] of Object.entries(subtree)) {
      if (id === parentId) continue;
      placed[id] = point;
    }
  }

  // RCCs sit in a bottom band under the tallest section subtree.
  if (rccIds.length > 0) {
    let bandTop = spinePos.y + spineNode.height + V_GAP;
    for (const id of Object.keys(placed)) {
      if (rccIds.includes(id) || id === spineId || id === director.id) continue;
      // Only measure seats that hang under the spine (sections / extras).
      const node = model.byId.get(id);
      const pos = placed[id];
      if (!node || !pos) continue;
      let walk: string | null | undefined = id;
      let underSpine = false;
      while (walk) {
        if (walk === spineId) {
          underSpine = true;
          break;
        }
        walk = model.byId.get(walk)?.parentId;
      }
      if (underSpine) {
        bandTop = Math.max(bandTop, pos.y + node.height + V_GAP);
      }
    }
    bandTop += SECTION_CROSSBAR_HEADROOM;

    const rccCrossbarY = (bandTop - SECTION_CROSSBAR_HEADROOM + bandTop) / 2;
    placeCrossbarRow({
      model,
      placed,
      ids: rccIds,
      centerX: spinePos.x + spineNode.width / 2,
      topY: bandTop,
      side: 'left',
    });

    const rccCrossbar = buildCrossbarConnectors(
      model,
      placed,
      spineId,
      rccIds,
      rccCrossbarY
    );
    Object.assign(edgeRoutes, rccCrossbar.edgeRoutes);
    Object.assign(edgeAttachments, rccCrossbar.edgeAttachments);
  }

  // Remaining roots (orphans / parallel trees) sit to the right.
  let cursorX =
    boundsOf(model, placed).maxX +
    (Object.keys(placed).length > 0 ? ROOT_GAP : 0);
  for (const orphanRoot of model.roots) {
    if (placed[orphanRoot]) continue;
    const subtree = layoutAlignedSubtree(
      model,
      orphanRoot,
      { x: 0, y: 0 },
      'left'
    );
    const { minX, maxX } = boundsOf(model, subtree);
    const shift = cursorX - minX;
    for (const [id, point] of Object.entries(subtree)) {
      placed[id] = { x: point.x + shift, y: point.y };
    }
    cursorX += maxX - minX + ROOT_GAP;
  }

  for (const node of input.nodes) {
    if (!placed[node.id]) placed[node.id] = { x: 0, y: 0 };
  }

  const resolved = resolveCollisions(
    model,
    placed,
    new Set([
      ...commandStaff,
      ...crossbarIds,
      ...rccIds,
      ...otherUnderDirector,
      ...model.roots,
    ])
  );

  // Rebuild connectors from final coordinates so elbows clear boxes.
  const finalRoutes: Record<string, FlowPoint[]> = {};
  const finalAttachments: Record<string, RosterEdgeAttachments> = {};

  Object.assign(
    finalRoutes,
    buildCommandStaffConnectors(model, resolved, director.id, commandStaff)
      .edgeRoutes
  );
  Object.assign(
    finalAttachments,
    buildCommandStaffConnectors(model, resolved, director.id, commandStaff)
      .edgeAttachments
  );

  if (dacoId && resolved[dacoId]) {
    const dacoEdgeId = rosterEdgeId(director.id, dacoId);
    finalAttachments[dacoEdgeId] = {
      source: { side: 'bottom', offset: 0.5 },
      target: { side: 'top', offset: 0.5 },
    };
  }

  const resolvedSpinePos = resolved[spineId];
  const resolvedSpineNode = model.byId.get(spineId);
  if (resolvedSpinePos && resolvedSpineNode && crossbarIds.length > 0) {
    const sectionYs = crossbarIds
      .map((id) => resolved[id]?.y)
      .filter((y): y is number => y != null);
    const sectionTop = Math.min(...sectionYs);
    const gapAbove = Math.max(
      resolvedSpinePos.y + resolvedSpineNode.height + V_GAP,
      sectionTop - SECTION_CROSSBAR_HEADROOM
    );
    const finalCrossbarY = (gapAbove + sectionTop) / 2;
    const crossbar = buildCrossbarConnectors(
      model,
      resolved,
      spineId,
      crossbarIds,
      finalCrossbarY
    );
    Object.assign(finalRoutes, crossbar.edgeRoutes);
    Object.assign(finalAttachments, crossbar.edgeAttachments);
  }

  if (resolvedSpinePos && resolvedSpineNode && rccIds.length > 0) {
    const rccYs = rccIds
      .map((id) => resolved[id]?.y)
      .filter((y): y is number => y != null);
    const rccTop = Math.min(...rccYs);
    const gapAbove = Math.max(
      resolvedSpinePos.y + resolvedSpineNode.height + V_GAP,
      rccTop - SECTION_CROSSBAR_HEADROOM
    );
    const finalRccBarY = (gapAbove + rccTop) / 2;
    const rccCrossbar = buildCrossbarConnectors(
      model,
      resolved,
      spineId,
      rccIds,
      finalRccBarY
    );
    Object.assign(finalRoutes, rccCrossbar.edgeRoutes);
    Object.assign(finalAttachments, rccCrossbar.edgeAttachments);
  }

  for (const branch of [
    { parents: crossbarIds, side: 'left' as AlignedSide },
    { parents: flanks.left, side: flankAlignSide('left') },
    { parents: flanks.right, side: flankAlignSide('right') },
  ]) {
    const aligned = branchConnectors(
      model,
      resolved,
      branch.parents,
      branch.side
    );
    Object.assign(finalRoutes, aligned.edgeRoutes);
    Object.assign(finalAttachments, aligned.edgeAttachments);
  }

  return {
    layoutPositions: resolved,
    edgeRoutes: finalRoutes,
    edgeAttachments: finalAttachments,
  };
}

/**
 * HWCG source control: IC (optional) → Source Control Branch Director → groups
 * and task forces in centered rows with a crossbar under each parent.
 */
export function layoutHwcgSourceControlOrgChart(input: {
  nodes: LayoutNode[];
}): LayoutResult {
  const model = buildModel(input.nodes, {});
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};

  const ic = input.nodes.find((node) => node.name === INCIDENT_COMMANDER_SEAT);
  const director = input.nodes.find((node) =>
    isHwcgSourceControlSeat(node.name)
  );
  const rootId = ic?.id ?? director?.id ?? model.roots[0];
  if (!rootId) {
    return { layoutPositions: {}, edgeRoutes, edgeAttachments };
  }

  const placed: Record<string, FlowPoint> = layoutCenteredSubtree(
    model,
    rootId,
    { x: 0, y: 0 }
  );

  let cursorX =
    boundsOf(model, placed).maxX +
    (Object.keys(placed).length > 0 ? ROOT_GAP : 0);
  for (const orphanRoot of model.roots) {
    if (placed[orphanRoot]) continue;
    const subtree = layoutCenteredSubtree(model, orphanRoot, { x: 0, y: 0 });
    const { minX, maxX } = boundsOf(model, subtree);
    const shift = cursorX - minX;
    for (const [id, point] of Object.entries(subtree)) {
      placed[id] = { x: point.x + shift, y: point.y };
    }
    cursorX += maxX - minX + ROOT_GAP;
  }

  for (const node of input.nodes) {
    if (!placed[node.id]) placed[node.id] = { x: 0, y: 0 };
  }

  const anchors = new Set<string>([
    ...model.roots,
    ...[...model.childrenOf.entries()]
      .filter(([, children]) => children.length > 0)
      .map(([id]) => id),
  ]);
  const resolved = resolveCollisions(model, placed, anchors);

  for (const [parentId, children] of model.childrenOf.entries()) {
    if (children.length === 0) continue;
    const parentPos = resolved[parentId];
    const parentNode = model.byId.get(parentId);
    const firstChild = resolved[children[0]];
    if (!parentPos || !parentNode || !firstChild) continue;
    const crossbarY = (parentPos.y + parentNode.height + firstChild.y) / 2;
    const crossbar = buildCrossbarConnectors(
      model,
      resolved,
      parentId,
      children,
      crossbarY
    );
    Object.assign(edgeRoutes, crossbar.edgeRoutes);
    Object.assign(edgeAttachments, crossbar.edgeAttachments);
  }

  return {
    layoutPositions: resolved,
    edgeRoutes,
    edgeAttachments,
  };
}

/**
 * Default ICS org chart: AoDC (optional) → IC → flanked command staff beside a
 * clear spine → shared crossbar to section chiefs → left-rail subtrees. HWCG
 * source-control charts use the centered branch layout instead.
 */
export function layoutIcsDefaultOrgChart(input: {
  nodes: LayoutNode[];
}): LayoutResult {
  if (input.nodes.some((node) => isTemaDirectorSeat(node.name))) {
    return layoutTemaEocOrgChart(input);
  }
  if (isHwcgSourceControlChart(input.nodes)) {
    return layoutHwcgSourceControlOrgChart(input);
  }

  const model = buildModel(input.nodes, {});
  const placed: Record<string, FlowPoint> = {};
  const edgeRoutes: Record<string, FlowPoint[]> = {};
  const edgeAttachments: Record<string, RosterEdgeAttachments> = {};

  const byName = (name: string) =>
    input.nodes.find((node) => node.name === name);

  const aodc = byName(AREA_OR_DISTRICT_COMMAND_SEAT);
  const ic = byName(INCIDENT_COMMANDER_SEAT);

  // Prefer IC as the visual root; keep AoDC above it when both exist.
  if (aodc && ic) {
    placed[aodc.id] = { x: 0, y: 0 };
    placed[ic.id] = {
      x: aodc.width / 2 - ic.width / 2,
      y: aodc.height + V_GAP,
    };
  } else if (ic) {
    placed[ic.id] = { x: 0, y: 0 };
  } else if (aodc) {
    placed[aodc.id] = { x: 0, y: 0 };
  }

  const rootId = ic?.id ?? aodc?.id ?? model.roots[0];
  if (!rootId) {
    return { layoutPositions: {}, edgeRoutes, edgeAttachments };
  }

  if (!placed[rootId]) {
    placed[rootId] = { x: 0, y: 0 };
  }

  const icNode = ic ?? model.byId.get(rootId);
  const icId = icNode?.id ?? rootId;
  const icPos = placed[icId];
  if (!icNode || !icPos) {
    return { layoutPositions: placed, edgeRoutes, edgeAttachments };
  }

  const icChildren = [...(model.childrenOf.get(icId) ?? [])].sort((a, b) =>
    compareIcsSeatNames(
      model.byId.get(a)?.name ?? '',
      model.byId.get(b)?.name ?? ''
    )
  );

  const commandStaff = icChildren.filter((id) =>
    isCommandStaffSeat(model.byId.get(id)?.name ?? '')
  );
  const sectionChiefs = icChildren.filter((id) =>
    isSectionChiefSeat(model.byId.get(id)?.name ?? '')
  );
  const otherDirect = icChildren.filter(
    (id) => !commandStaff.includes(id) && !sectionChiefs.includes(id)
  );

  const flanks = placeFlankedCommandStaff(
    model,
    placed,
    commandStaff,
    icPos,
    icNode.width,
    icNode.height
  );

  // Section chiefs sit visually under the command-staff band but still report
  // to IC. The band clears the staff's own reports, not just their boxes, so
  // the crossbar is never drawn through them.
  const sectionGapAbove =
    commandStaff.length > 0
      ? flanks.bottom + V_GAP
      : icPos.y + icNode.height + V_GAP;
  const sectionBandTop = sectionGapAbove + SECTION_CROSSBAR_HEADROOM;
  const crossbarY = (sectionGapAbove + sectionBandTop) / 2;

  const sectionRowIds = [...sectionChiefs, ...otherDirect];
  placeCrossbarRow({
    model,
    placed,
    ids: sectionRowIds,
    centerX: icPos.x + icNode.width / 2,
    topY: sectionBandTop,
    side: 'left',
  });

  // Left-align everything under each section chief.
  for (const parentId of sectionRowIds) {
    const parentPos = placed[parentId];
    if (!parentPos) continue;
    const subtree = layoutAlignedSubtree(model, parentId, parentPos, 'left');
    for (const [id, point] of Object.entries(subtree)) {
      if (id === parentId) continue;
      placed[id] = point;
    }
  }

  // Any remaining roots (orphans / parallel trees) sit to the right.
  let cursorX =
    boundsOf(model, placed).maxX +
    (Object.keys(placed).length > 0 ? ROOT_GAP : 0);
  for (const orphanRoot of model.roots) {
    if (placed[orphanRoot]) continue;
    const subtree = layoutAlignedSubtree(
      model,
      orphanRoot,
      { x: 0, y: 0 },
      'left'
    );
    const { minX, maxX } = boundsOf(model, subtree);
    const shift = cursorX - minX;
    for (const [id, point] of Object.entries(subtree)) {
      placed[id] = { x: point.x + shift, y: point.y };
    }
    cursorX += maxX - minX + ROOT_GAP;
  }

  for (const node of input.nodes) {
    if (!placed[node.id]) placed[node.id] = { x: 0, y: 0 };
  }

  // Resolve overlaps first, then build elbows from final coordinates so the
  // spine / crossbar stay aligned with the boxes they must clear.
  const resolved = resolveCollisions(
    model,
    placed,
    new Set([...commandStaff, ...sectionRowIds, ...model.roots])
  );

  const staffConnectors = buildCommandStaffConnectors(
    model,
    resolved,
    icId,
    commandStaff
  );
  Object.assign(edgeRoutes, staffConnectors.edgeRoutes);
  Object.assign(edgeAttachments, staffConnectors.edgeAttachments);

  if (sectionRowIds.length > 0) {
    const crossbar = buildCrossbarConnectors(
      model,
      resolved,
      icId,
      sectionRowIds,
      crossbarY
    );
    Object.assign(edgeRoutes, crossbar.edgeRoutes);
    Object.assign(edgeAttachments, crossbar.edgeAttachments);
  }

  for (const branch of [
    { parents: sectionRowIds, side: 'left' as AlignedSide },
    { parents: flanks.left, side: flankAlignSide('left') },
    { parents: flanks.right, side: flankAlignSide('right') },
  ]) {
    const aligned = branchConnectors(
      model,
      resolved,
      branch.parents,
      branch.side
    );
    Object.assign(edgeRoutes, aligned.edgeRoutes);
    Object.assign(edgeAttachments, aligned.edgeAttachments);
  }

  return {
    layoutPositions: resolved,
    edgeRoutes,
    edgeAttachments,
  };
}

/**
 * Recomputes seat coordinates in the requested style. With a selection, only
 * the selected branches move and everything else stays put; with nothing
 * selected the whole chart is rebuilt, laying independent trees out side by
 * side. "Centered" (no selection) uses the ICS default layout, except HWCG
 * source-control charts which stay on the centered branch layout.
 */
export function applyRosterLayoutStyle(input: {
  style: RosterLayoutStyle;
  nodes: LayoutNode[];
  selectedIds?: string[];
  existingLayout: Record<string, FlowPoint>;
  existingEdgeRoutes?: Record<string, FlowPoint[]>;
  existingEdgeAttachments?: Record<string, RosterEdgeAttachments>;
}): LayoutResult {
  const { style, nodes, existingLayout } = input;
  const known = new Set(nodes.map((node) => node.id));
  const selectedIds = (input.selectedIds ?? []).filter((id) => known.has(id));

  if (style === 'centered' && selectedIds.length === 0) {
    if (isHwcgSourceControlChart(nodes)) {
      return layoutHwcgSourceControlOrgChart({ nodes });
    }
    return layoutIcsDefaultOrgChart({ nodes });
  }

  const model = buildModel(nodes, existingLayout);

  let positions: Record<string, FlowPoint>;
  // Which seats this pass actually placed. With a selection that is only the
  // chosen branches, and their connectors are the only ones entitled to change.
  const restyled = new Set<string>();

  if (selectedIds.length === 0) {
    positions = {};
    let cursorX = 0;
    for (const rootId of model.roots) {
      const subtree = layoutSubtree(model, style, rootId, { x: 0, y: 0 });
      const { minX, maxX } = boundsOf(model, subtree);
      const shift = cursorX - minX;
      for (const [id, position] of Object.entries(subtree)) {
        positions[id] = { x: position.x + shift, y: position.y };
        restyled.add(id);
      }
      cursorX += maxX - minX + ROOT_GAP;
    }
    // A seat left out by a cycle still needs somewhere to be.
    for (const node of nodes) {
      if (!positions[node.id])
        positions[node.id] = existingLayout[node.id] ?? { x: 0, y: 0 };
    }
  } else {
    const parentById = Object.fromEntries(
      nodes.map((node) => [node.id, node.parentId])
    );
    positions = { ...existingLayout };
    for (const rootId of getLayoutSelectionRoots(selectedIds, parentById)) {
      const rootPosition = positions[rootId] ?? { x: 0, y: 0 };
      const subtree = layoutSubtree(model, style, rootId, rootPosition);
      Object.assign(positions, subtree);
      for (const id of Object.keys(subtree)) restyled.add(id);
    }
  }

  // A seat deleted since the restyle would otherwise keep a zero-sized box in
  // the collision pass and push its neighbours around from nowhere.
  for (const id of Object.keys(positions)) {
    if (!known.has(id)) delete positions[id];
  }

  // Seats added since the restyle have nowhere to be yet. Relaying out the
  // nearest placed ancestor drops them onto that branch's rail rather than
  // leaving them stacked on the origin.
  const unplaced = nodes.filter((node) => !positions[node.id]);
  if (unplaced.length > 0) {
    const anchors = new Set<string>();
    const orphans: string[] = [];

    for (const node of unplaced) {
      let cursor = node.parentId;
      const seen = new Set<string>([node.id]);
      while (cursor && !seen.has(cursor) && !positions[cursor]) {
        seen.add(cursor);
        cursor = model.byId.get(cursor)?.parentId ?? null;
      }
      if (cursor && positions[cursor]) anchors.add(cursor);
      else orphans.push(node.id);
    }

    for (const anchorId of anchors) {
      const subtree = layoutSubtree(
        model,
        style,
        anchorId,
        positions[anchorId]
      );
      Object.assign(positions, subtree);
      for (const id of Object.keys(subtree)) restyled.add(id);
    }

    let cursorX = boundsOf(model, positions).maxX + ROOT_GAP;
    for (const orphanId of orphans) {
      if (positions[orphanId]) continue;
      const subtree = layoutSubtree(model, style, orphanId, { x: 0, y: 0 });
      const { minX, maxX } = boundsOf(model, subtree);
      const shift = cursorX - minX;
      for (const [id, point] of Object.entries(subtree)) {
        positions[id] = { x: point.x + shift, y: point.y };
        restyled.add(id);
      }
      cursorX += maxX - minX + ROOT_GAP;
    }
  }

  const resolved = resolveCollisions(
    model,
    positions,
    new Set([
      ...model.roots,
      ...model.roots.flatMap((rootId) => model.childrenOf.get(rootId) ?? []),
    ])
  );
  const edgeRoutes = { ...(input.existingEdgeRoutes ?? {}) };
  const edgeAttachments = { ...(input.existingEdgeAttachments ?? {}) };

  if (isAlignedStyle(style)) {
    const aligned = alignedConnectors(model, resolved, style, restyled);
    return {
      layoutPositions: resolved,
      edgeRoutes: { ...edgeRoutes, ...aligned.edgeRoutes },
      edgeAttachments: { ...edgeAttachments, ...aligned.edgeAttachments },
    };
  }

  // Centered draws plain hierarchy lines, so the restyled branches give their
  // routing back to the default rather than keeping an outline's elbows.
  for (const node of model.byId.values()) {
    if (!node.parentId || !restyled.has(node.id)) continue;
    const edgeId = rosterEdgeId(node.parentId, node.id);
    delete edgeRoutes[edgeId];
    delete edgeAttachments[edgeId];
  }

  return { layoutPositions: resolved, edgeRoutes, edgeAttachments };
}
